import { postHogLoggingService, type LogLevel, type LogService } from './posthog-logging-service'

// TODO: move to structured logging (stable error codes / taxonomy in `context`
// instead of ad-hoc message strings + shape-sniffing like isTimeoutError) so
// failure classes can be queried and alerted on directly instead of by message text.

/**
 * Writes to the console and hands the line to the PostHog logging service,
 * which owns everything PostHog sees (OTLP log records + `$exception` events).
 * The service is a no-op when no transport is registered, so this stays safe
 * in scripts, tests and the Edge runtime.
 */
function log(
  level: LogLevel,
  service: LogService,
  message: string,
  context?: Record<string, unknown>,
  error?: unknown
) {
  const parts = [`[${service}] ${level} ${message}`]
  if (context) parts.push(JSON.stringify(context))
  const fn = level === 'ERROR' ? console.error : level === 'WARN' ? console.warn : console.log
  fn(parts.join(' '))

  postHogLoggingService.record({ level, service, message, context, error })
}

export const logger = {
  info: (service: LogService, message: string, context?: Record<string, unknown>) => log('INFO', service, message, context),
  warn: (service: LogService, message: string, context?: Record<string, unknown>) => log('WARN', service, message, context),
  /**
   * Logs an ERROR and captures it as a PostHog exception.
   *
   * Note the third argument: unlike `info`/`warn`, it is the caught value, not
   * the context. Inside a `catch`, always pass it — the real error is what
   * gives PostHog its constructor name and stack, so `$exception_type` and
   * `$exception_message` describe the actual failure instead of falling back
   * to the generic `LoggedError` type. Its message is merged into the context
   * as `error`, so there is no need to add that yourself.
   *
   * ```ts
   * logger.error('dynamo', 'putSurvey failed', err, { ms })  // in a catch
   * logger.error('routine', 'created product missing review', undefined, { productId })
   * ```
   */
  error: (service: LogService, message: string, err?: unknown, context?: Record<string, unknown>) =>
    log('ERROR', service, message, err === undefined ? context : { ...context, error: errorMessage(err) }, err),
}

/** Human-readable message for an unknown caught value. */
export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

export function redactEmail(email: string): string {
  const at = email.indexOf('@')
  return at > 0 ? `***@${email.slice(at + 1)}` : '***'
}
